import React, { useState, useEffect, useRef } from 'react';
import './Drones.css';
import droneSwarmWebp from '../../../assets/images/drone_swarm_full.webp';
import droneSwarmPng from '../../../assets/images/drone_swarm_full.png';
import droneObserverWebp from '../../../assets/images/drone_observer_full.webp';
import droneObserverPng from '../../../assets/images/drone_observer_full.png';
import droneRecyclerWebp from '../../../assets/images/drone_recycler_full.webp';
import droneRecyclerPng from '../../../assets/images/drone_recycler_full.png';

const droneData = [
  {
    id: 'swarm',
    title: 'Swarm',
    description: `Armed with a small lazer, these critters are mass produced and scattered
      throughout the city. Once they rally and call their comrades, they become a multitude
      capable of melding down anything in their path.`,
  },
  {
    id: 'observer',
    title: 'Observer',
    description: `A mix of surveillance and companion drones. Whether replaying recorded
      actions or returning damage tenfold, the Observer waits for the best moment to leverage
      its stored experiences. A tactical mind is needed to master this drone.`,
  },
  {
    id: 'recycler',
    title: 'Recycler',
    description: `Acting as repair drones, the Recyclers can retool parts from fallen enemies
      for repeated use. Within the burrows, they fix walls and machinery, ensuring nothing
      is wasted.`,
  }
];

const Drones = () => {
  const titleContainerRef = useRef(null);
  const carouselRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && titleContainerRef.current) {
        titleContainerRef.current.classList.add('active');
      }
    }, { threshold: 0.99 });
    if (titleContainerRef.current) observer.observe(titleContainerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer2 = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && carouselRef.current) {
        carouselRef.current.classList.add('fade-in');
      }
    }, { threshold: 0.2 });

    if (carouselRef.current) observer2.observe(carouselRef.current);
    return () => observer2.disconnect();
  }, []);

  const cardRef1 = useRef(null);
  const cardRef2 = useRef(null);
  const cardRef3 = useRef(null);

  const imageRef1 = useRef(null);
  const imageRef2 = useRef(null);
  const imageRef3 = useRef(null);

  const [isFlipped1, setIsFlipped1] = useState(false);
  const [isFlipped2, setIsFlipped2] = useState(false);
  const [isFlipped3, setIsFlipped3] = useState(false);

  let xRotation = 0;
  let yRotation = 0;

  const handleMouseMove = (event, cardRef, isFlipped) => {
    const { clientX, clientY } = event;
    const { top, left, width, height } = cardRef.current.getBoundingClientRect();
    const xPos = clientX - left - width / 2;
    const yPos = clientY - top - height / 2;

    xRotation = (yPos / (height / 2)) * -12;
    yRotation = (xPos / (width / 2)) * 17;

    // Get the current Y translation from the hover animation
    const currentTransform = window.getComputedStyle(cardRef.current).transform;
    const matrix = new DOMMatrix(currentTransform);
    const currentY = matrix.m42; // Get the Y translation value

    if (isFlipped) {
      cardRef.current.style.transform = `translateY(${currentY}px) rotateX(${xRotation}deg) rotateY(${yRotation + 180}deg) scale(1)`;
    } else {
      cardRef.current.style.transform = `translateY(${currentY}px) rotateX(0deg) rotateY(${yRotation}deg) scale(1)`;
    }
    
    cardRef.current.style.setProperty('--mouse-x', xPos + 'px');
    cardRef.current.style.setProperty('--mouse-y', yPos + 'px');

    if (Math.abs(xRotation) > 14 || Math.abs(yRotation) > 15) {
      cardRef.current.classList.add('shine');
    } else {
      cardRef.current.classList.remove('shine');
    }
  };

  const handleMouseLeave = (cardRef, setIsFlipped) => {
    // add a gentle transition back
    cardRef.current.style.transition = 'transform 0.3s ease-out';

    cardRef.current.style.transform = 'rotateX(0deg) rotateY(0deg)';
    cardRef.current.classList.remove('shine');
    const backEl = cardRef.current.querySelector('.card-back');
    if (backEl) backEl.style.visibility = 'hidden';
    setIsFlipped(false);

    setTimeout(() => {
      // remove the transition so mouse move remains instant
      cardRef.current.style.transition = 'none';
      cardRef.current.style.setProperty('--mouse-x', '0px');
      cardRef.current.style.setProperty('--mouse-y', '0px');
    }, 300);
  };

  // Generate 10 squares for top-left pulsing
  const [squares] = useState(
    Array.from({ length: 10 }, (_, i) => ({ id: i, delay: Math.random() * 2 }))
  );

  return (
    <div className="drone-page-container">
      <div className="title-container-drones" ref={titleContainerRef} style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <h2 className="fns-title-font-character">Partners of Salvation.<span className="cursor">|</span></h2>
        <div className="title-container-background"></div>
      </div>
      <div className="drone-carousel-wrapper" ref={carouselRef}>
        {/* Add background element similar to Characters.css */}
        <div className="drone-carousel-background"></div>
        
        <div className="drone-pulse-squares">
          {squares.map((sq) => (
            <div
              key={sq.id}
              className="pulse-square"
              style={{ animationDelay: `${sq.delay}s` }}
            />
          ))}
        </div>
        <div className="drone-list">
          <div className="drone-tilt-container">
            <div className="drone-image-container">
              <picture>
                <source srcSet={droneSwarmWebp} type="image/webp" />
                <img
                  ref={imageRef1}
                  src={droneSwarmPng}
                  alt="Swarm Drone"
                  className="drone-placeholder"
                  onMouseEnter={(e) => handleMouseMove(e, imageRef1, isFlipped1)}
                  onMouseMove={(e) => handleMouseMove(e, imageRef1, isFlipped1)}
                  onMouseLeave={() => handleMouseLeave(imageRef1, setIsFlipped1)}
                />
              </picture>
              <div className="digital-lines-overlay"></div>
            </div>
            <div className="drone-card">
              <h3>Swarm</h3>
              <p>Armed with a small lazer, these critters are mass produced and scattered
                throughout the city. Once they rally and call their comrades, they become a multitude
                capable of melding down anything in their path.</p>
            </div>
          </div>

          <div className="drone-tilt-container">
            <div className="drone-image-container">
              <picture>
                <source srcSet={droneObserverWebp} type="image/webp" />
                <img
                  ref={imageRef2}
                  src={droneObserverPng}
                  alt="Observer Drone"
                  className="drone-placeholder"
                  onMouseEnter={(e) => handleMouseMove(e, imageRef2, isFlipped2)}
                  onMouseMove={(e) => handleMouseMove(e, imageRef2, isFlipped2)}
                  onMouseLeave={() => handleMouseLeave(imageRef2, setIsFlipped2)}
                />
              </picture>
              <div className="digital-lines-overlay"></div>
            </div>
            <div className="drone-card">
              <h3>Observer</h3>
              <p>A mix of surveillance and companion drones. Whether replaying recorded
                actions or returning damage tenfold, the Observer waits for the best moment to leverage
                its stored experiences. A tactical mind is needed to master this drone.</p>
            </div>
          </div>

          <div className="drone-tilt-container">
            <div className="drone-image-container">
              <picture>
                <source srcSet={droneRecyclerWebp} type="image/webp" />
                <img
                  ref={imageRef3}
                  src={droneRecyclerPng}
                  alt="Recycler Drone"
                  className="drone-placeholder"
                  onMouseEnter={(e) => handleMouseMove(e, imageRef3, isFlipped3)}
                  onMouseMove={(e) => handleMouseMove(e, imageRef3, isFlipped3)}
                  onMouseLeave={() => handleMouseLeave(imageRef3, setIsFlipped3)}
                />
              </picture>
              <div className="digital-lines-overlay"></div>
            </div>
            <div className="drone-card">
              <h3>Recycler</h3>
              <p>Acting as repair drones, the Recyclers can retool parts from fallen enemies
                for repeated use. Within the burrows, they fix walls and machinery, ensuring nothing
                is wasted.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Drones;